import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import * as Api from "../lib/api";
import { POSEIDON } from "../lib/defaults";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { loginAs } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await Api.login({ email, password });
      loginAs(d.access_token, d.user, d.team);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  }

  return (
    <AuthShell title="Log In" subtitle="Access your dealer dashboard">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} testId="login-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testId="login-password" required />
        <button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full h-16 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="w-5 h-5 animate-spin" />}
          Log In
        </button>
        <div className="text-right -mt-2">
          <Link to="/forgot-password" data-testid="link-forgot-password" className="text-[11px] font-mono tracking-[0.15em] uppercase text-white/50 hover:text-[#F26522] transition-colors">
            Forgot password?
          </Link>
        </div>
        <div className="text-center pt-4 border-t border-white/10">
          <span className="text-white/60 text-sm">No account yet?</span>{" "}
          <Link to="/signup" data-testid="link-signup" className="text-[#F26522] hover:underline font-bold">Start a Team</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 bg-white p-1">
              <img src={POSEIDON.logo} alt="Poseidon" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading text-2xl font-black uppercase tracking-tight text-white">POSEIDON</div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#F26522]">PRODOSE</div>
            </div>
          </div>
          <div className="bg-[#111111] border-2 border-white/15 p-7">
            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-white">{title}</h1>
            <p className="text-white/60 text-sm mt-1 mb-6">{subtitle}</p>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testId, required, autoComplete, placeholder }) {
  return (
    <div className="space-y-2">
      <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/70 font-semibold block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-14 px-4 border-2 border-white/15 bg-black text-base font-mono text-white focus:border-[#F26522] focus:outline-none transition-colors"
      />
    </div>
  );
}
